import React from 'react';
import { cn } from '../../utils/cn';

const SkeletonLoader = ({ className = '', variant = 'default', ...props }) => {
  const variants = {
    default: 'h-4 w-full',
    text: 'h-4 w-full',
    title: 'h-6 md:h-7 w-3/4',
    circle: 'rounded-full',
    avatar: 'w-10 h-10 rounded-full',
    card: 'h-48 md:h-56 w-full',
    button: 'h-10 w-32 rounded-lg',
  };

  return (
    <div
      className={cn(
        'animate-shimmer bg-gradient-to-r from-muted via-white to-muted bg-[length:200%_100%] rounded',
        variants?.[variant],
        className
      )}
      {...props}
    />
  );
};

export const StoryCardSkeleton = () => {
  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <SkeletonLoader variant="card" className="rounded-none" />
      <div className="p-4 md:p-5 space-y-3">
        <SkeletonLoader variant="title" />
        <SkeletonLoader variant="text" />
        <SkeletonLoader variant="text" className="w-5/6" />
        <div className="flex items-center gap-3 pt-2">
          <SkeletonLoader variant="avatar" />
          <SkeletonLoader className="h-4 w-32" />
        </div>
        <div className="flex items-center gap-4 pt-2">
          <SkeletonLoader className="h-3 w-24" />
          <SkeletonLoader className="h-3 w-20" />
        </div>
      </div>
    </div>
  );
};

export const ProfileSkeleton = () => {
  return (
    <div className="bg-card rounded-xl border border-border p-6 space-y-4">
      <div className="flex items-center gap-4">
        <SkeletonLoader className="w-20 h-20 rounded-full" />
        <div className="flex-1 space-y-2">
          <SkeletonLoader className="h-6 w-48" />
          <SkeletonLoader className="h-4 w-64" />
        </div>
      </div>
      <SkeletonLoader className="h-4 w-full" />
      <SkeletonLoader className="h-4 w-5/6" />
      <div className="flex gap-4 pt-2">
        <SkeletonLoader className="h-10 w-32 rounded-lg" />
        <SkeletonLoader className="h-10 w-32 rounded-lg" />
      </div>
    </div>
  );
};

export const ChartSkeleton = () => {
  return (
    <div className="bg-card rounded-xl border border-border p-6 space-y-4">
      <SkeletonLoader className="h-6 w-48" />
      <SkeletonLoader className="h-64 w-full rounded-lg" />
    </div>
  );
};

export const CommentSkeleton = () => {
  return (
    <div className="flex gap-3 py-4">
      <SkeletonLoader variant="avatar" />
      <div className="flex-1 space-y-2">
        <SkeletonLoader className="h-4 w-32" />
        <SkeletonLoader className="h-4 w-full" />
        <SkeletonLoader className="h-4 w-4/5" />
        <div className="flex gap-4 pt-2">
          <SkeletonLoader className="h-8 w-16 rounded" />
          <SkeletonLoader className="h-8 w-16 rounded" />
        </div>
      </div>
    </div>
  );
};

export const TableRowSkeleton = () => {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-border">
      <SkeletonLoader className="h-4 flex-1" />
      <SkeletonLoader className="h-4 w-20" />
      <SkeletonLoader className="h-4 w-20" />
      <SkeletonLoader className="h-4 w-20" />
    </div>
  );
};

export const PublicationCardSkeleton = () => {
  return (
    <div className="bg-card rounded-xl border border-border p-5 space-y-4">
      <div className="flex items-start gap-4">
        <SkeletonLoader className="w-16 h-16 rounded-lg" />
        <div className="flex-1 space-y-2">
          <SkeletonLoader className="h-5 w-48" />
          <SkeletonLoader className="h-4 w-full" />
        </div>
      </div>
      <div className="flex gap-4">
        <SkeletonLoader className="h-4 w-24" />
        <SkeletonLoader className="h-4 w-24" />
      </div>
      <SkeletonLoader className="h-10 w-full rounded-lg" />
    </div>
  );
};

export default SkeletonLoader;